/* RESPONSIBLE TEAM: team-reporting */
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type { InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';
import type Admin from 'embercom/models/admin';
import type Role from 'embercom/models/role';
import { getRoleNameById } from 'embercom/utils/teammates/role-dropdown-utils';

function getExistingUserIds(store: Store, schedule: ScheduledExternalShare): number[] {
  let userIds = schedule.recipients?.userIds ?? [];
  return userIds.filter((id: number) => store.peekRecord('admin', String(id)) !== null);
}

function getExistingRoleIds(store: Store, schedule: ScheduledExternalShare): number[] {
  let roleIds = schedule.recipients?.roles || [];
  return roleIds.filter((id: number) => store.peekRecord('role', String(id)) !== null);
}

function getUserName(store: Store, intl: IntlService, userId: number): string {
  let admin = store.peekRecord('admin', String(userId)) as Admin | null;
  return admin?.name || String(intl.t('reporting.manage-schedules.user-fallback', { id: userId }));
}

export function getResolvedAdmins(store: Store, schedule: ScheduledExternalShare): Admin[] {
  return getExistingUserIds(store, schedule)
    .map((id: number) => store.peekRecord('admin', String(id)) as Admin | null)
    .filter((admin): admin is Admin => admin !== null);
}

export function getResolvedUserNames(
  store: Store,
  intl: IntlService,
  schedule: ScheduledExternalShare,
): string[] {
  return getExistingUserIds(store, schedule).map((id: number) => getUserName(store, intl, id));
}

export function getResolvedRoleNames(
  store: Store,
  intl: IntlService,
  schedule: ScheduledExternalShare,
): string[] {
  let allRoles = store.peekAll('role').slice() as Role[];
  return getExistingRoleIds(store, schedule).map((id: number) =>
    getRoleNameById(id, allRoles, intl),
  );
}

export function getRolesData(store: Store, intl: IntlService, schedule: ScheduledExternalShare) {
  let roleNames = getResolvedRoleNames(store, intl, schedule);
  let count = roleNames.length;
  return {
    count,
    title: intl.t('reporting.manage-schedules.recipients-tooltip.role', { count }),
    items: roleNames.map((roleName) => ({
      text: roleName,
      icon: 'multiple-people' as InterfaceIconName,
    })),
  };
}

export function getUsersData(store: Store, intl: IntlService, schedule: ScheduledExternalShare) {
  let admins = getResolvedAdmins(store, schedule);
  let count = admins.length;
  return {
    count,
    title: intl.t('reporting.manage-schedules.recipients-tooltip.user', { count }),
    items: admins.map((admin) => ({
      text: admin.name,
      avatar: admin,
    })),
  };
}

export function getEmailsData(intl: IntlService, schedule: ScheduledExternalShare) {
  let emails = schedule.recipients?.emails || [];
  let count = emails.length;
  return {
    count,
    title: intl.t('reporting.manage-schedules.recipients-tooltip.email', { count }),
    items: emails.map((email) => ({
      text: email,
      icon: 'email' as InterfaceIconName,
    })),
  };
}
